import { useState, useEffect } from "react";
import { Button, Control } from "./components";
import { accountRequest, googleStart, type AccountView } from "./account-api";
export type AccountWork = (
  work: () => Promise<unknown>,
  success?: string,
) => Promise<void>;
export function ConfirmAccountPassword({ run }: { run: AccountWork }) {
  const [password, setPassword] = useState("");
  return (
    <form
      className="account-reauth"
      onSubmit={(event) => {
        event.preventDefault();
        void run(async () => {
          await accountRequest("/reauthenticate", { password });
          setPassword("");
        }, "Password confirmed for sensitive changes.");
      }}
    >
      <Control label="Confirm current password">
        <input
          required
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Control>
      <Button type="submit">Confirm password</Button>
    </form>
  );
}
export function AccountFactors({
  view,
  run,
  onRecovery,
  forced = false,
}: {
  view: AccountView;
  run: AccountWork;
  onRecovery: (codes: string[]) => void;
  forced?: boolean;
}) {
  const [totp, setTotp] = useState<{
      id: string;
      secret: string;
      qr: string;
    }>(),
    [totpCode, setTotpCode] = useState(""),
    [emailChallenge, setEmailChallenge] = useState<string>(),
    [emailCode, setEmailCode] = useState("");
  const factor = view.twoFactor!;
  return (
    <section aria-label="Two-factor settings">
      <h2>
        {forced
          ? "Set up two-factor authentication"
          : "Two-factor authentication"}
      </h2>
      <p>
        {factor.required
          ? "Your account or workspace requires a second factor before workspace access."
          : "Add an email code or an authenticator app to protect your account."}
      </p>
      {factor.methods.includes("totp") && (
        <div className="account-section">
          <h3>Authenticator app</h3>
          <p>
            Use Google Authenticator, Microsoft Authenticator, 1Password or
            another TOTP app.
          </p>
          {!totp && (
            <div className="button-row">
              <Button
                onClick={() =>
                  void run(async () =>
                    setTotp(await accountRequest("/two-factor/totp/start", {})),
                  )
                }
              >
                {factor.enrolled.totp
                  ? "Replace authenticator"
                  : "Set up authenticator"}
              </Button>
              {factor.enrolled.totp && !forced && (
                <Button
                  onClick={() =>
                    void run(
                      () =>
                        accountRequest("/two-factor/disable", {
                          method: "totp",
                        }),
                      "Authenticator removed.",
                    )
                  }
                >
                  Remove authenticator
                </Button>
              )}
            </div>
          )}
          {totp && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void run(async () => {
                  const result = await accountRequest<{
                    recoveryCodes: string[];
                  }>("/two-factor/totp/confirm", {
                    id: totp.id,
                    code: totpCode,
                  });
                  setTotp(undefined);
                  setTotpCode("");
                  onRecovery(result.recoveryCodes);
                }, "Authenticator enabled.");
              }}
            >
              <img
                className="account-qr"
                src={totp.qr}
                width={240}
                height={240}
                alt="Scan this QR code with your authenticator app"
              />
              <Control label="Authenticator setup key">
                <input readOnly value={totp.secret} autoComplete="off" />
              </Control>
              <Control label="New authenticator code">
                <input
                  required
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  value={totpCode}
                  onChange={(event) => setTotpCode(event.target.value)}
                />
              </Control>
              <div className="button-row">
                <Button kind="primary" type="submit">
                  Verify authenticator
                </Button>
                <Button onClick={() => setTotp(undefined)}>
                  Cancel authenticator setup
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
      {factor.methods.includes("email") && (
        <div className="account-section">
          <h3>Email codes</h3>
          <p>
            Verification codes go to {view.user?.email}.{" "}
            {factor.emailAvailable
              ? "Uses the email provider assigned to this site."
              : "An administrator must configure an email provider first."}
          </p>
          <div className="button-row">
            <Button
              disabled={!factor.emailAvailable}
              onClick={() =>
                void run(async () => {
                  const result = await accountRequest<{ id: string }>(
                    "/two-factor/email",
                    { enroll: true },
                  );
                  setEmailChallenge(result.id);
                }, "Enrollment code sent.")
              }
            >
              {factor.enrolled.email
                ? "Verify email codes again"
                : "Enable email codes"}
            </Button>
            {factor.enrolled.email && !forced && (
              <Button
                onClick={() =>
                  void run(
                    () =>
                      accountRequest("/two-factor/disable", {
                        method: "email",
                      }),
                    "Email factor removed.",
                  )
                }
              >
                Remove email factor
              </Button>
            )}
          </div>
          {emailChallenge && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void run(async () => {
                  const result = await accountRequest<{
                    recoveryCodes: string[];
                  }>("/two-factor/verify", {
                    method: "email",
                    code: emailCode,
                    id: emailChallenge,
                    enroll: true,
                  });
                  setEmailChallenge(undefined);
                  setEmailCode("");
                  onRecovery(result.recoveryCodes);
                }, "Email two-factor authentication enabled.");
              }}
            >
              <Control label="Email enrollment code">
                <input
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={emailCode}
                  onChange={(event) => setEmailCode(event.target.value)}
                />
              </Control>
              <Button kind="primary" type="submit">
                Verify email enrollment
              </Button>
            </form>
          )}
        </div>
      )}
      {!forced && (factor.enrolled.email || factor.enrolled.totp) && (
        <Button
          onClick={() =>
            void run(async () =>
              onRecovery(
                (
                  await accountRequest<{ recoveryCodes: string[] }>(
                    "/two-factor/recovery",
                    {},
                  )
                ).recoveryCodes,
              ),
            )
          }
        >
          Generate new recovery codes
        </Button>
      )}
    </section>
  );
}
export function AccountChallenge({
  view,
  run,
  onRecovery,
}: {
  view: AccountView;
  run: AccountWork;
  onRecovery: (codes: string[]) => void;
}) {
  const methods = view.twoFactor!.methods.filter(
      (method) => view.twoFactor!.enrolled[method],
    ),
    [method, setMethod] = useState<"email" | "totp" | "recovery">(
      methods[0] || "recovery",
    ),
    [code, setCode] = useState(""),
    [challenge, setChallenge] = useState<string>();
  return (
    <>
      {methods.length > 0 && (
        <section>
          <h2>Verify your sign-in</h2>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void run(async () => {
                await accountRequest("/two-factor/verify", {
                  method,
                  code,
                  ...(method === "email" ? { id: challenge } : {}),
                });
                setCode("");
              }, "Sign-in verified.");
            }}
          >
            <Control label="Verification method">
              <select
                value={method}
                onChange={(event) => {
                  setMethod(event.target.value as typeof method);
                  setCode("");
                }}
              >
                {methods.map((method) => (
                  <option key={method} value={method}>
                    {method === "totp" ? "Authenticator app" : "Email code"}
                  </option>
                ))}
                <option value="recovery">Saved recovery code</option>
              </select>
            </Control>
            {method === "email" && (
              <Button
                onClick={() =>
                  void run(
                    async () =>
                      setChallenge(
                        (
                          await accountRequest<{ id: string }>(
                            "/two-factor/email",
                            { enroll: false },
                          )
                        ).id,
                      ),
                    "Verification code sent.",
                  )
                }
              >
                Send sign-in code
              </Button>
            )}
            <Control label="Verification code">
              <input
                required
                autoComplete={method === "recovery" ? "off" : "one-time-code"}
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </Control>
            <Button type="submit" kind="primary">
              Verify sign-in
            </Button>
          </form>
        </section>
      )}
      {methods.length === 0 && (
        <AccountFactors view={view} run={run} onRecovery={onRecovery} forced />
      )}
      <ConfirmAccountPassword run={run} />
      <Button onClick={() => void run(() => accountRequest("/logout", {}))}>
        Sign out
      </Button>
    </>
  );
}
export function AccountSecurity({
  view,
  run,
  onRecovery,
}: {
  view: AccountView;
  run: AccountWork;
  onRecovery: (codes: string[]) => void;
}) {
  const [currentPassword, setCurrentPassword] = useState(""),
    [password, setPassword] = useState(""),
    [confirmation, setConfirmation] = useState(""),
    [sessions, setSessions] = useState<
      { id: string; current: boolean; createdAt: number; expiresAt: number }[]
    >([]);
  useEffect(() => {
    void accountRequest<typeof sessions>("/sessions")
      .then(setSessions)
      .catch(() => {});
  }, [view.user?.id]);
  return (
    <main className="account-content">
      <h1>Account & security</h1>
      <p>
        {view.user?.name} · {view.user?.email}
      </p>
      <ConfirmAccountPassword run={run} />
      <section className="account-section">
        <h2>Google account</h2>
        <p>
          {view.user?.googleLinked
            ? "Your Google account is connected. You can use it to sign in."
            : "Connect Google after signing in to this account. Email matching alone does not connect accounts."}
        </p>
        <div className="button-row">
          <Button
            disabled={!view.googleLoginAvailable}
            onClick={() => void run(() => googleStart("link"))}
          >
            {view.user?.googleLinked
              ? "Reconnect Google account"
              : "Connect Google account"}
          </Button>
          {view.user?.googleLinked && (
            <Button
              onClick={() =>
                void run(
                  () => accountRequest("/google/unlink", {}),
                  "Google account disconnected.",
                )
              }
            >
              Disconnect Google account
            </Button>
          )}
        </div>
        {!view.googleLoginAvailable && (
          <small>
            An application administrator must configure Google OAuth credentials
            under Administration.
          </small>
        )}
      </section>
      <AccountFactors view={view} run={run} onRecovery={onRecovery} />
      <section className="account-section">
        <h2>Change password</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              if (password !== confirmation)
                throw new Error("The new passwords do not match.");
              await accountRequest("/password/change", {
                currentPassword,
                password,
              });
              setPassword("");
              setConfirmation("");
              setCurrentPassword("");
              setSessions(await accountRequest("/sessions"));
            }, "Password changed. Other sessions were revoked.");
          }}
        >
          <Control label="Current password">
            <input
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </Control>
          <Control label="New password">
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              maxLength={128}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Control>
          <Control label="Confirm new password">
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </Control>
          <Button type="submit">Change password</Button>
        </form>
      </section>
      <section className="account-section">
        <h2>Active sessions</h2>
        {sessions.map((session) => (
          <div className="account-session" key={session.id}>
            <span>
              {session.current ? "This session" : "Other session"} · expires{" "}
              {new Intl.DateTimeFormat(view.settings.locale, {
                dateStyle: "medium",
                timeStyle: "short",
                timeZone: view.settings.timezone,
              }).format(new Date(session.expiresAt))}
            </span>
            <Button
              onClick={() =>
                void run(async () => {
                  await accountRequest("/sessions/revoke", { id: session.id });
                  setSessions(await accountRequest("/sessions"));
                }, "Session revoked.")
              }
            >
              Revoke session
            </Button>
          </div>
        ))}
      </section>
    </main>
  );
}
